import type {
  CallParticipant,
  CallProviderFactory,
  CallProviderInput,
  CallSnapshot,
  ClientCallProvider,
  MediaDevice,
} from './call.types'
import { IDLE_CALL_SNAPSHOT } from './call.types'

interface DailyTrack {
  state?: string
  persistentTrack?: MediaStreamTrack
  track?: MediaStreamTrack
}

interface DailyParticipant {
  session_id: string
  user_name?: string
  local?: boolean
  audio?: boolean
  video?: boolean
  screen?: boolean
  /** Set by Daily from the joining token's is_owner claim (server-derived). */
  owner?: boolean
  tracks?: { audio?: DailyTrack; video?: DailyTrack; screenVideo?: DailyTrack }
}

interface DailyCall {
  join(input: { url: string; token: string }): Promise<unknown>
  leave(): Promise<unknown>
  destroy(): Promise<unknown>
  startCamera(input?: unknown): Promise<unknown>
  setLocalAudio(on: boolean): unknown
  setLocalVideo(on: boolean): unknown
  startScreenShare(): unknown
  stopScreenShare(): unknown
  participants(): Record<string, DailyParticipant>
  on(event: string, callback: (event?: unknown) => void): unknown
  enumerateDevices(): Promise<{ devices: Array<{ deviceId: string; label?: string; kind?: string }> }>
  setInputDevicesAsync(input: { videoDeviceId?: string; audioDeviceId?: string }): Promise<unknown>
  setOutputDeviceAsync(input: { outputDeviceId: string }): Promise<unknown>
}

export type DailyFailure = 'expired' | 'not_started' | 'ended' | 'denied' | 'device_error'

export function classifyDailyFailure(error: unknown): DailyFailure {
  const value = error as { message?: string; errorMsg?: string; error?: { type?: string } }
  const kind = value?.error?.type ?? ''
  const message = `${value?.message ?? ''} ${value?.errorMsg ?? ''}`
  if (kind === 'exp-token' || kind === 'exp-room') return 'expired'
  if (kind === 'nbf-token') return 'not_started'
  if (kind === 'no-room' || kind === 'meeting-not-started') return 'ended'
  if (/token|auth|denied|permission/i.test(message)) return 'denied'
  return 'device_error'
}

function isValidInput(input: CallProviderInput): boolean {
  try {
    const url = new URL(input.roomUrl)
    return url.protocol === 'https:' && input.token.length >= 16 && input.token.length <= 8192
  } catch {
    return false
  }
}

async function loadDaily(): Promise<(props?: unknown) => DailyCall> {
  const module = (await import('@daily-co/daily-js')) as unknown as {
    default?: { createCallObject?: (props?: unknown) => DailyCall }
    createCallObject?: (props?: unknown) => DailyCall
  }
  const daily = module.default ?? module
  const create = daily.createCallObject?.bind(daily)
  if (!create) throw new Error('daily_sdk_unavailable')
  return create
}

function liveTrack(track?: DailyTrack): MediaStreamTrack | undefined {
  if (!track || track.state === 'off' || track.state === 'blocked') return undefined
  return track.persistentTrack ?? track.track
}

export const createDailyCallProvider: CallProviderFactory = (input): ClientCallProvider => {
  let call: DailyCall | null = null
  let destroyed = false
  let activeSpeaker: string | null = null
  let snapshot: CallSnapshot = { ...IDLE_CALL_SNAPSHOT, local: { ...IDLE_CALL_SNAPSHOT.local } }
  let previewPromise: Promise<void> | null = null
  let joinPromise: Promise<void> | null = null
  const subscribers = new Set<(value: CallSnapshot) => void>()

  const emit = (patch: Partial<CallSnapshot>) => {
    if (destroyed && patch.phase !== 'left' && patch.phase !== 'ended') return
    snapshot = { ...snapshot, ...patch }
    for (const callback of subscribers) callback(snapshot)
  }

  const mapParticipants = () => {
    if (!call) return
    const participants: CallParticipant[] = Object.values(call.participants()).map((participant) => ({
      sessionId: participant.session_id,
      name: participant.local
        ? input.displayName || participant.user_name || 'You'
        : participant.user_name || 'Guest',
      isLocal: Boolean(participant.local),
      audioOn: Boolean(participant.audio),
      videoOn: Boolean(participant.video),
      screenOn: Boolean(participant.screen),
      isActiveSpeaker: activeSpeaker === participant.session_id,
      isOwner: Boolean(participant.owner),
      videoTrack: liveTrack(participant.tracks?.video),
      audioTrack: participant.local ? undefined : liveTrack(participant.tracks?.audio),
      screenTrack: liveTrack(participant.tracks?.screenVideo),
    }))
    participants.sort((left, right) => Number(right.isLocal) - Number(left.isLocal))
    const local = participants.find((participant) => participant.isLocal)
    emit({
      participants,
      local: {
        micOn: local?.audioOn ?? snapshot.local.micOn,
        camOn: local?.videoOn ?? snapshot.local.camOn,
        screenOn: local?.screenOn ?? snapshot.local.screenOn,
      },
    })
  }

  const applyFailure = (error: unknown) => {
    const failure = classifyDailyFailure(error)
    if (failure === 'expired') {
      emit({ phase: 'expired', errorCode: 'token_expired', message: 'The scoped join token expired. Return to Conference Calls and join again.' })
    } else if (failure === 'not_started') {
      emit({ phase: 'expired', errorCode: 'meeting_not_started', message: 'This meeting is not open yet.' })
    } else if (failure === 'ended') {
      emit({ phase: 'ended', errorCode: 'room_unavailable', message: 'This room is no longer available.' })
    } else if (failure === 'denied') {
      emit({ phase: 'denied', errorCode: 'join_denied', message: 'The server-scoped room token denied access.' })
    } else {
      emit({ phase: 'device_error', errorCode: 'provider_or_device_error', message: 'The media provider or a local device reported an error.' })
    }
  }

  const bind = (instance: DailyCall) => {
    const refresh = () => mapParticipants()
    instance.on('joined-meeting', () => { emit({ phase: 'joined', message: null, errorCode: undefined }); refresh() })
    instance.on('participant-joined', refresh)
    instance.on('participant-updated', refresh)
    instance.on('participant-left', refresh)
    instance.on('track-started', refresh)
    instance.on('track-stopped', refresh)
    instance.on('active-speaker-change', (event) => {
      const value = event as { activeSpeaker?: { peerId?: string } }
      activeSpeaker = value?.activeSpeaker?.peerId ?? null
      refresh()
    })
    instance.on('left-meeting', () => emit({ phase: snapshot.phase === 'ended' ? 'ended' : 'left', participants: [] }))
    instance.on('network-connection', (event) => {
      const value = event as { type?: string; event?: string }
      const type = value?.type ?? value?.event
      if (type === 'interrupted') emit({ phase: 'reconnecting' })
      if (type === 'connected') emit({ phase: 'joined' })
    })
    instance.on('network-quality-change', (event) => {
      const value = event as { threshold?: string }
      emit({ networkWarning: value?.threshold === 'low' || value?.threshold === 'very-low' })
    })
    instance.on('camera-error', applyFailure)
    instance.on('error', applyFailure)
  }

  return {
    subscribe(callback) {
      subscribers.add(callback)
      callback(snapshot)
      return () => subscribers.delete(callback)
    },
    getSnapshot() { return snapshot },
    async preview() {
      if (previewPromise) return previewPromise
      if (call || destroyed || typeof window === 'undefined') return
      previewPromise = (async () => {
        if (!isValidInput(input)) {
          emit({ phase: 'denied', errorCode: 'invalid_join_session', message: 'The join session is invalid or incomplete.' })
          return
        }
        emit({ phase: 'loading_sdk', message: null, errorCode: undefined })
        try {
          const create = await loadDaily()
          call = create({ subscribeToTracksAutomatically: true })
          bind(call)
          emit({ phase: 'requesting_permissions' })
          await call.startCamera()
          mapParticipants()
          emit({ phase: 'preview_ready', message: null })
        } catch (error) {
          applyFailure(error)
          const failedCall = call
          call = null
          try { await failedCall?.leave() } catch { /* permission/provider failure cleanup continues */ }
          try { await failedCall?.destroy() } catch { /* permission/provider failure cleanup continues */ }
        }
      })().finally(() => { previewPromise = null })
      return previewPromise
    },
    async join() {
      if (joinPromise) return joinPromise
      joinPromise = (async () => {
        if (!call) await this.preview()
        if (!call || destroyed) return
        emit({ phase: 'joining', message: null, errorCode: undefined })
        try {
          await call.join({ url: input.roomUrl, token: input.token })
        } catch (error) {
          applyFailure(error)
        }
      })().finally(() => { joinPromise = null })
      return joinPromise
    },
    async setMic(on) {
      if (!call || destroyed) return
      try {
        await Promise.resolve(call.setLocalAudio(on))
        emit({ local: { ...snapshot.local, micOn: on } })
      } catch (error) { applyFailure(error) }
    },
    async setCam(on) {
      if (!call || destroyed) return
      try {
        await Promise.resolve(call.setLocalVideo(on))
        emit({ local: { ...snapshot.local, camOn: on } })
      } catch (error) { applyFailure(error) }
    },
    async setScreen(on) {
      if (!call || destroyed) return
      try {
        await Promise.resolve(on ? call.startScreenShare() : call.stopScreenShare())
        emit({ local: { ...snapshot.local, screenOn: on } })
      } catch {
        emit({ message: 'Screen sharing was cancelled or is unavailable.', errorCode: 'screen_share_unavailable' })
      }
    },
    async leave() {
      if (destroyed) return
      destroyed = true
      try { await call?.leave() } catch { /* cleanup continues */ }
      try { await call?.destroy() } catch { /* cleanup continues */ }
      call = null
      emit({ phase: snapshot.phase === 'ended' ? 'ended' : 'left', participants: [], local: { micOn: false, camOn: false, screenOn: false } })
    },
    async listDevices(): Promise<MediaDevice[]> {
      if (!call) return []
      try {
        const { devices } = await call.enumerateDevices()
        return devices
          .filter((device) => device.deviceId && (device.kind === 'videoinput' || device.kind === 'audioinput' || device.kind === 'audiooutput'))
          .map((device) => ({
            deviceId: device.deviceId,
            label: device.label || (device.kind ?? 'device'),
            kind: device.kind as MediaDevice['kind'],
          }))
      } catch {
        return []
      }
    },
    async setCamera(deviceId: string) {
      if (!call || destroyed) return
      try { await call.setInputDevicesAsync({ videoDeviceId: deviceId }) } catch (error) { applyFailure(error) }
    },
    async setMicrophone(deviceId: string) {
      if (!call || destroyed) return
      try { await call.setInputDevicesAsync({ audioDeviceId: deviceId }) } catch (error) { applyFailure(error) }
    },
    async setSpeaker(deviceId: string) {
      if (!call || destroyed) return
      try { await call.setOutputDeviceAsync({ outputDeviceId: deviceId }) } catch { /* speaker selection is best-effort */ }
    },
  }
}
